import { useState } from "react";
import { useCart } from "../context/CartContext";
import { api, formatINR } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [method, setMethod] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const field = "w-full bg-transparent border-b border-mj-border py-3 font-sans-mj text-mj-text placeholder:text-mj-muted/60 focus:outline-none focus:border-mj-red transition-colors";

  const cartPayload = items.map((i) => ({ product_id: i.id, quantity: i.quantity }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your cart is empty."); return; }
    setLoading(true);
    try {
      if (method === "cod") {
        const { data } = await api.post("/orders", { items: cartPayload, ...form, payment_method: "cod" });
        clear();
        navigate(`/payment/success?cod=1&order=${data.order_id}`);
      } else {
        const { data: order } = await api.post("/orders", { items: cartPayload, ...form, payment_method: "stripe" });
        const { data } = await api.post("/checkout/session", { items: cartPayload, origin_url: window.location.origin, order_id: order.order_id });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error("Could not process. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="pt-40 min-h-screen text-center px-6">
        <h1 className="font-serif-mj text-4xl text-mj-text">Your cart is empty</h1>
        <Link to="/collection" className="inline-block mt-8 bg-mj-red text-mj-bg px-8 py-4 overline hover:bg-mj-reddark transition-colors">Explore the Collection</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-32 min-h-screen">
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-32">
        <Reveal><h1 className="font-serif-mj tracking-tight text-mj-text text-5xl md:text-6xl mb-16">Checkout</h1></Reveal>
        <div className="grid md:grid-cols-2 gap-16">
          {/* Form */}
          <form onSubmit={submit} className="flex flex-col gap-7">
            <input data-testid="co-name" required placeholder="Full name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="co-email" type="email" required placeholder="Email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="co-phone" required placeholder="Phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <textarea data-testid="co-address" required placeholder="Delivery address" rows={3} className={field} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />

            <div className="mt-4">
              <p className="overline text-mj-muted mb-4">Payment Method</p>
              <RadioGroup value={method} onValueChange={setMethod} className="flex flex-col gap-3">
                <label data-testid="pay-stripe" className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${method === "stripe" ? "border-mj-red bg-mj-surface/50" : "border-mj-border"}`}>
                  <RadioGroupItem value="stripe" />
                  <span className="font-serif-mj text-xl">Pay Online (Card)</span>
                </label>
                <label data-testid="pay-cod" className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${method === "cod" ? "border-mj-red bg-mj-surface/50" : "border-mj-border"}`}>
                  <RadioGroupItem value="cod" />
                  <span className="font-serif-mj text-xl">Cash on Delivery</span>
                </label>
              </RadioGroup>
            </div>

            <button data-testid="place-order-btn" disabled={loading} type="submit" className="bg-mj-red text-mj-bg py-4 overline hover:bg-mj-reddark transition-colors disabled:opacity-60 mt-4">
              {loading ? "Processing…" : method === "cod" ? "Place Order" : "Pay Securely"}
            </button>
          </form>

          {/* Summary */}
          <div className="bg-mj-surface/50 border border-mj-border p-8 h-fit">
            <h2 className="font-serif-mj text-2xl mb-6">Order Summary</h2>
            <div className="divide-y divide-mj-border">
              {items.map((i) => (
                <div key={i.id} className="py-4 flex justify-between gap-4">
                  <span className="font-sans-mj text-sm">{i.name} <span className="text-mj-muted">× {i.quantity}</span></span>
                  <span className="font-sans-mj text-sm whitespace-nowrap">{formatINR(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-baseline border-t border-mj-border mt-4 pt-6">
              <span className="overline text-mj-muted">Total</span>
              <span data-testid="checkout-total" className="font-serif-mj text-3xl text-mj-red">{formatINR(total)}</span>
            </div>
            <p className="text-xs text-mj-muted mt-6">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
          </div>
        </div>
      </section>
    </div>
  );
}
